import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  CheckCircle2, 
  ChevronDown,
  ArrowRight,
  Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import axios from 'axios';
import { API } from '@/App';
import LocationMap from '@/components/LocationMap';

// Simple markdown to HTML converter
const formatMarkdown = (content) => {
  if (!content) return '';
  
  // Process line by line for better control
  const lines = content.split('\n');
  const result = [];
  let inList = false;
  let listType = null; // 'ul' or 'ol'
  let inTable = false;
  
  for (let line of lines) {
    // Skip empty lines
    if (line.trim() === '') {
      if (inList) {
        result.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      if (inTable) {
        result.push('</tbody></table>');
        inTable = false;
      }
      result.push('');
      continue;
    }
    
    // Headers
    if (line.startsWith('### ')) {
      if (inList) { result.push(`</${listType}>`); inList = false; listType = null; }
      if (inTable) { result.push('</tbody></table>'); inTable = false; }
      result.push(`<h3 class="text-xl font-semibold text-slate-900 mt-8 mb-4">${line.slice(4)}</h3>`);
      continue;
    }
    if (line.startsWith('## ')) {
      if (inList) { result.push(`</${listType}>`); inList = false; listType = null; }
      if (inTable) { result.push('</tbody></table>'); inTable = false; }
      result.push(`<h2 class="text-2xl font-bold text-slate-900 mt-10 mb-4">${line.slice(3)}</h2>`);
      continue;
    }
    
    // Table rows - skip separator rows with ---
    if (line.includes('|') && line.includes('---')) {
      continue;
    }
    
    // Table rows (with pipes)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      if (inList) { result.push(`</${listType}>`); inList = false; listType = null; }
      const cells = line.split('|').filter(c => c.trim());
      if (!inTable) {
        result.push('<table class="w-full border-collapse my-4"><tbody>');
        inTable = true;
      }
      const cellsHtml = cells.map(c => `<td class="border border-slate-200 px-4 py-2 text-slate-600">${c.trim()}</td>`).join('');
      result.push(`<tr>${cellsHtml}</tr>`);
      continue;
    }
    
    // Unordered list items (-)
    if (line.startsWith('- ')) {
      if (inTable) { result.push('</tbody></table>'); inTable = false; }
      if (inList && listType === 'ol') { result.push('</ol>'); inList = false; }
      if (!inList) { result.push('<ul class="list-disc list-inside space-y-2 my-4 ml-4">'); inList = true; listType = 'ul'; }
      let item = line.slice(2);
      item = item.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
      result.push(`<li class="text-slate-600">${item}</li>`);
      continue;
    }
    
    // Ordered list items (1. 2. 3.)
    if (/^\d+\. /.test(line)) {
      if (inTable) { result.push('</tbody></table>'); inTable = false; }
      if (inList && listType === 'ul') { result.push('</ul>'); inList = false; }
      if (!inList) { result.push('<ol class="list-decimal list-inside space-y-2 my-4 ml-4">'); inList = true; listType = 'ol'; }
      let item = line.replace(/^\d+\. /, '');
      item = item.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
      result.push(`<li class="text-slate-600">${item}</li>`);
      continue;
    }
    
    // Regular paragraph
    if (inList) { result.push(`</${listType}>`); inList = false; listType = null; }
    if (inTable) { result.push('</tbody></table>'); inTable = false; }
    let para = line;
    para = para.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
    para = para.replace(/\*(.*?)\*/g, '<em>$1</em>');
    result.push(`<p class="text-slate-600 mb-4 leading-relaxed">${para}</p>`);
  }
  
  if (inList) result.push(`</${listType}>`);
  if (inTable) result.push('</tbody></table>');
  
  return result.join('\n');
};

const LocationPage = () => {
  const { slug } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState([]);
  const [totalEstimate, setTotalEstimate] = useState('2.500 - 3.500 €');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/locations/${slug}`)
      .then(res => {
        setLocation(res.data);
        document.title = res.data.meta_title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', res.data.meta_description);
      })
      .catch(() => setLocation(null))
      .finally(() => setLoading(false));
    
    // Load prices for this location
    axios.get(`${API}/license-classes/location/${slug}`)
      .then(res => {
        if (res.data && res.data.length > 0) {
          // Find Class B for this location
          const classB = res.data.find(lc => lc.class_type === 'B') || res.data[0];
          if (classB && classB.cost_components) {
            const priceList = classB.cost_components.map(comp => ({
              name: comp.label,
              price: `${Math.round(comp.price)} €`
            }));
            setPrices(priceList);
            
            // Calculate total estimate
            const total = classB.cost_components.reduce((sum, comp) => {
              if (comp.is_per_unit) {
                return sum + (comp.price * (comp.default_units || 10));
              }
              return sum + comp.price;
            }, 0);
            const minTotal = Math.round(total * 0.85);
            const maxTotal = Math.round(total * 1.15);
            setTotalEstimate(`${minTotal.toLocaleString('de-DE')} - ${maxTotal.toLocaleString('de-DE')} €`);
          }
        }
      })
      .catch(() => {
        // Fallback prices if API fails
        setPrices([
          { name: 'Grundbetrag', price: '380 €' },
          { name: 'Übungsfahrt (45 Min.)', price: '65 €' },
          { name: 'Sonderfahrt (45 Min.)', price: '75 €' },
          { name: 'Theorieprüfung zzgl. TÜV-Gebühr', price: '100 €' },
          { name: 'Praktische Prüfung zzgl. TÜV-Gebühr', price: '190 €' },
        ]);
      });
  }, [slug]);

  // Add LocalBusiness Schema for SEO
  useEffect(() => {
    if (!location) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "DrivingSchool",
      "name": `Fahrschule Rößner ${location.name}`,
      "description": location.meta_description,
      "url": `https://www.roessnerfahrschule.de/standorte/${slug}`,
      "telephone": location.phone,
      "email": location.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": location.address.split(',')[0],
        "addressLocality": location.city,
        "postalCode": location.address.match(/\d{5}/)?.[0] || "",
        "addressCountry": "DE"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": location.lat,
        "longitude": location.lng
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": location.opening_hours?.mo_fr?.split(' - ')[0] || "09:00",
          "closes": location.opening_hours?.mo_fr?.split(' - ')[1] || "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": location.opening_hours?.sa?.split(' - ')[0] || "10:00",
          "closes": location.opening_hours?.sa?.split(' - ')[1] || "14:00"
        }
      ],
      "priceRange": "€€",
      "image": "https://www.roessnerfahrschule.de/logo.png",
      "sameAs": [
        "https://www.facebook.com/fahrschuleroessner",
        "https://www.instagram.com/fahrschuleroessner"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "127",
        "bestRating": "5"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Führerschein-Ausbildung",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Führerschein Klasse B",
              "description": "PKW-Führerschein Ausbildung"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Intensivkurs",
              "description": "Führerschein in 2-4 Wochen"
            }
          }
        ]
      }
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    scriptTag.id = 'location-schema';
    document.head.appendChild(scriptTag);

    return () => {
      const existingScript = document.getElementById('location-schema');
      if (existingScript) existingScript.remove();
    };
  }, [location, slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Standort nicht gefunden</h1>
          <Link to="/">
            <Button>Zur Startseite</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="location-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 to-slate-800" data-testid="location-hero">
        <div className="absolute inset-0 bg-grid opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-blue-400 mb-4">
              <MapPin className="w-5 h-5" />
              <span className="font-medium">{location.city}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {location.hero_title}
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              {location.hero_subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
                <Button 
                  type="submit"
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
                  data-testid="location-cta"
                >
                  Jetzt anmelden <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
              <a href={`tel:${location.phone.replace(/\s/g, '')}`}>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  {location.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-200">
            <div className="py-6 px-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Adresse</div>
                <div className="font-medium text-slate-900">{location.address}</div>
              </div>
            </div>
            <div className="py-6 px-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Telefon</div>
                <div className="font-medium text-slate-900">{location.phone}</div>
                {location.mobile && (
                  <div className="text-sm text-slate-600">{location.mobile}</div>
                )}
              </div>
            </div>
            <div className="py-6 px-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500">E-Mail</div>
                <div className="font-medium text-slate-900">{location.email}</div>
              </div>
            </div>
            <div className="py-6 px-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Öffnungszeiten</div>
                {location.opening_hours.mo_fr && (
                  <div className="font-medium text-slate-900">Mo-Fr: {location.opening_hours.mo_fr}</div>
                )}
                {location.opening_hours.di_fr && (
                  <div className="font-medium text-slate-900">Di-Fr: {location.opening_hours.di_fr}</div>
                )}
                {location.opening_hours.sa && location.opening_hours.sa !== 'Geschlossen' && (
                  <div className="text-sm text-slate-600">Sa: {location.opening_hours.sa}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Content Column */}
            <div className="lg:col-span-2">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: formatMarkdown(location.content) }}
                data-testid="location-content"
              />

              {/* Features */}
              <div className="mt-12" data-testid="location-features">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Vorteile dieses Standorts</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {location.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ Section */}
              <div className="mt-12" data-testid="location-faq">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Häufige Fragen</h2>
                <Accordion type="single" collapsible className="w-full">
                  {location.faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left font-medium">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                {/* Price Card */}
                <Card data-testid="location-prices">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-4">Preisübersicht</h3>
                    <div className="space-y-3">
                      {prices.map((item, index) => (
                        <div key={index} className="price-row">
                          <span className="text-slate-600">{item.name}</span>
                          <span className="font-semibold text-slate-900">{item.price}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                      <div className="text-sm text-slate-600 mb-1">Geschätzte Gesamtkosten</div>
                      <div className="text-2xl font-bold text-blue-600">{totalEstimate}</div>
                      <div className="text-xs text-slate-500 mt-1">Je nach Fahrstundenanzahl</div>
                    </div>
                    <Link to="/kontakt" className="block mt-6">
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                        Kostenlose Beratung
                      </Button>
                    </Link>
                  </CardContent>
                </Card>

                {/* Map */}
                <Card data-testid="location-map">
                  <CardContent className="p-0 overflow-hidden rounded-xl">
                    <LocationMap 
                      slug={slug} 
                      address={location.address} 
                      name={location.name} 
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Starte jetzt in {location.name}!
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Vereinbare ein kostenloses Beratungsgespräch und erfahre alles über deine Führerscheinausbildung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
              <Button 
                type="submit"
                size="lg" 
                className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 rounded-full px-8"
              >
                Jetzt anmelden <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
